<script lang="ts">
  import { costsApi, ApiError, type CostDetail, type CostScope } from '../lib/api';
  import { CostDetailView } from '../lib/cost-detail-view';
  import { CostsPeriod } from '../lib/costs-period';
  import { liveCosts } from '../lib/live-costs.svelte';
  import { Format } from '../lib/format';
  import CostBars from '../components/CostBars.svelte';
  import CostDetailSkeleton from '../components/CostDetailSkeleton.svelte';

  interface Props {
    scope: CostScope;
    id: string;
    period: CostsPeriod;
    onBack: () => void;
    onOpenWorkflow: (id: string) => void;
    onUnauthorized: () => void;
    onLive?: (live: boolean) => void;
  }

  let { scope, id, period, onBack, onOpenWorkflow, onUnauthorized, onLive }: Props = $props();

  let data = $state<CostDetail | null>(null);
  let refetching = $state(false);
  let error = $state<string | null>(null);

  const view = $derived(data ? CostDetailView.of(data) : null);

  $effect(() => {
    void load(scope, id, period);
  });

  // O detalhe recebe o mesmo aviso de gasto novo que a lista: sem isto o número
  // do cliente aberto ficava parado enquanto o selo dizia "ao vivo".
  liveCosts({
    period: () => period,
    reload: () => void load(scope, id, period),
    onLive: (connected) => onLive?.(connected),
  });

  async function load(alvo: CostScope, alvoId: string, range: CostsPeriod): Promise<void> {
    refetching = true;
    error = null;
    try {
      data = await costsApi.detail(alvo, alvoId, range.from, range.to);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onUnauthorized();
        return;
      }
      if (err instanceof ApiError && err.status === 404) {
        data = null;
        error = 'Sem gasto neste período.';
        return;
      }
      error = err instanceof Error ? err.message : 'erro ao carregar';
    } finally {
      refetching = false;
    }
  }
</script>

<div class="detail">
  <nav class="crumbs" aria-label="Onde você está">
    <button class="crumb" onclick={onBack}>Custos</button>
    <span class="sep" aria-hidden="true">/</span>
    <span class="crumb current">{view?.name ?? '…'}</span>
  </nav>

  {#if error}
    <p class="state state-error" role="alert">{error}</p>
    <button class="back" onclick={onBack}>Voltar para a tabela</button>
  {:else if !view}
    <CostDetailSkeleton />
  {:else}
    <div class="content" class:refetching>
      <header class="head">
        <div>
          <h2 class="title">{view.name}</h2>
          <p class="subtitle mono">
            {view.subtitle ? `${view.subtitle} · ` : ''}{view.periodLabel}
          </p>
        </div>
        <div class="headline">
          <span class="headline-value">{Format.brl(view.total.brl)}</span>
          <span class="headline-sub mono">{Format.usd(view.total.usd)} no período</span>
        </div>
      </header>

      <div class="cards">
        <div class="card">
          <span class="card-label">Média por dia</span>
          <span class="card-value">{view.dailyAverageLabel}</span>
        </div>
        <div class="card">
          <span class="card-label">Média por rodada</span>
          <span class="card-value">{view.averageLabel}</span>
          <span class="card-sub mono">{Format.count(view.total.runs)} rodada(s)</span>
        </div>
        <div class="card">
          <span class="card-label">Chamadas ao modelo</span>
          <span class="card-value">{Format.count(view.total.calls)}</span>
        </div>
        <div class="card">
          <span class="card-label">Cache hit</span>
          <span class="card-value">{view.cacheHitLabel}</span>
          <span class="card-sub mono">{Format.count(view.total.tokens.prompt)} tok in</span>
        </div>
        <div class="card">
          <span class="card-label">Raciocínio</span>
          <span class="card-value">{view.reasoningLabel}</span>
          <span class="card-sub mono">da saída cobrada</span>
        </div>
      </div>

      <div class="panels">
        <CostBars
          title="Onde o gasto acontece"
          bars={view.actionBars()}
          empty="Sem detalhe por ação nesta origem."
        />
        <CostBars
          title="Por passo do workflow"
          bars={view.stepBars()}
          empty="Sem passo identificado."
        />
        <CostBars title="Por dia" bars={view.dayBars()} empty="Sem gasto no período." />
        {#if view.isClient}
          <CostBars
            title="Por workflow"
            bars={view.workflowBars()}
            empty="Nenhum workflow no período."
            onOpen={onOpenWorkflow}
          />
        {/if}
        <CostBars title="Por modelo" bars={view.modelBars()} empty="Sem modelo identificado." />
        <CostBars
          title="Rodadas mais caras"
          bars={view.runBars()}
          empty="As rotinas nossas não têm rodada individual."
        />
      </div>
    </div>
  {/if}
</div>

<style>
  .detail {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
  }
  .crumbs {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: 12px;
  }
  .crumb {
    padding: 0;
    font: inherit;
    color: var(--text-tertiary);
    background: none;
    border: none;
    cursor: pointer;
  }
  .crumb:hover {
    color: var(--text-secondary);
  }
  .crumb:focus-visible {
    outline: 2px solid var(--color-success);
    outline-offset: 2px;
  }
  .crumb.current {
    color: var(--text-primary);
    cursor: default;
  }
  .sep {
    color: var(--text-tertiary);
  }
  .content {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
    transition: opacity var(--duration-normal, 200ms) var(--easing-default);
  }
  .content.refetching {
    opacity: 0.45;
  }
  .head {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: var(--space-4);
  }
  .title {
    margin: 0;
    font-size: 26px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--text-primary);
  }
  .subtitle {
    margin: 2px 0 0;
    font-size: 12px;
    color: var(--text-tertiary);
  }
  .headline {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }
  .headline-value {
    font-size: 34px;
    font-weight: 600;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    color: var(--text-primary);
  }
  .headline-sub {
    font-size: 11px;
    color: var(--text-tertiary);
  }
  .cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: var(--space-4);
  }
  .card {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-4) var(--space-5);
    background: var(--surface-card);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-sm);
  }
  .card-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-tertiary);
  }
  .card-value {
    font-size: 28px;
    font-weight: 600;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    color: var(--text-primary);
  }
  .card-sub {
    font-size: 11px;
    color: var(--text-tertiary);
  }
  /* Coluna em vez de grade: os painéis têm alturas muito diferentes — "por dia"
     tem trinta linhas e "rodadas mais caras" tem duas —, e numa grade cada linha
     fica com a altura do maior, abrindo buracos entre os cartões. */
  .panels {
    columns: 3 340px;
    column-gap: var(--space-4);
  }
  .panels > :global(*) {
    break-inside: avoid;
    margin-bottom: var(--space-4);
  }
  .state {
    color: var(--text-tertiary);
    text-align: center;
    margin: var(--space-7) 0 0;
  }
  .state-error {
    color: var(--color-danger);
  }
  .back {
    align-self: center;
    font-size: 12px;
    color: var(--text-secondary);
    background: none;
    border: none;
    cursor: pointer;
    text-decoration: underline;
  }
  .back:focus-visible {
    outline: 2px solid var(--color-success);
    outline-offset: 2px;
  }
</style>
